import { useEffect, useState } from "react";
import { Link, Navigate, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import Header from "@/components/Header";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { api, clearAuth, getUser } from "@/lib/api";
import { MapPin, Plus, Repeat, Gift, Trash2, Home, Briefcase, Star, Download, AlertTriangle, ShieldOff, Copy, Share2, Bell } from "lucide-react";
import AddressAutocomplete from "@/components/AddressAutocomplete";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";

const STATUS_COLORS = {
  pending: "bg-amber-400/20 text-amber-300 border-amber-400/40",
  assigned: "bg-blue-400/20 text-blue-300 border-blue-400/40",
  in_progress: "bg-violet-400/20 text-violet-300 border-violet-400/40",
  completed: "bg-emerald-400/20 text-emerald-300 border-emerald-400/40",
  cancelled: "bg-red-400/20 text-red-300 border-red-400/40",
};

export default function MyRides() {
  const user = getUser();
  const nav = useNavigate();const [bookings, setBookings] = useState([]);
  const [me, setMe] = useState(null);
  const [newAddr, setNewAddr] = useState({ label: "Home", address: "" });
  const [confirmDelete, setConfirmDelete] = useState(false);
  const [dsarBusy, setDsarBusy] = useState(false);

  const load = async () => {
    try {
      const [b, m] = await Promise.all([api.get("/rider/bookings"), api.get("/rider/me")]);
      setBookings(b.data); setMe(m.data);
    } catch { toast.error("Failed to load"); }
  };
  useEffect(() => { if (user?.role === "rider") load(); }, []);

  if (!user) return <Navigate to="/login" replace />;
  if (user.role !== "rider") return <Navigate to="/" replace />;

  const addAddr = async (e) => {
    e.preventDefault();
    if (!newAddr.address) return;
    try { await api.post("/rider/addresses", newAddr); toast.success("Saved"); setNewAddr({ label: "Home", address: "" }); load(); }
    catch { toast.error("Failed"); }
  };
  const delAddr = async (id) => {
    try { await api.delete(`/rider/addresses/${id}`); toast.success("Removed"); load(); } catch { toast.error("Failed"); }
  };
  const rebook = (b) => nav("/book", { state: { pickup_location: b.pickup_location, dropoff_location: b.dropoff_location } });

  // ---- DSAR (Privacy Act / GDPR) self-serve ----
  const downloadData = async () => {
    setDsarBusy(true);
    try {
      const { data } = await api.get("/rider/me/export");
      const blob = new Blob([JSON.stringify(data, null, 2)], { type: "application/json" });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url; a.download = `triptap-my-data-${new Date().toISOString().slice(0,10)}.json`;
      a.click(); URL.revokeObjectURL(url);
      toast.success("Your data is downloading");
    } catch { toast.error("Could not export — try again"); }
    finally { setDsarBusy(false); }
  };
  const deleteAccount = async () => {
    setDsarBusy(true);
    try {
      await api.post("/rider/me/delete");
      toast.success("Account anonymised. See you again if you ever come back.");
      clearAuth();
      setTimeout(() => nav("/", { replace: true }), 800);
    } catch { toast.error("Deletion failed — please contact support"); setDsarBusy(false); }
  };

  return (
    <div className="min-h-screen bg-[#090D14] text-white">
      <Header />
      <div className="max-w-4xl mx-auto px-4 pt-28 pb-20">
        <div className="text-caption mb-2">Your account</div>
        <h1 className="font-display text-4xl font-extrabold">Kia ora, {user.name}</h1>

        {/* Referral card — pinned right under the greeting so it's the first thing riders see */}
        {referral && referral.code && (
          <div className="mt-6 glass-card rounded-2xl p-5 gold-border" data-testid="referral-card">
            <div className="flex items-start gap-4 flex-wrap">
              <div className="w-11 h-11 rounded-lg bg-gradient-to-br from-red-500 to-red-700 grid place-items-center shrink-0"><Gift className="w-5 h-5 text-white" /></div>
              <div className="flex-1 min-w-0">
                <div className="font-display font-bold text-white">Give NZ$5, get NZ$5</div>
                <p className="text-sm text-slate-400 mt-0.5">Share your code — when a friend takes their first ride, you both get NZ$5 off your next one.</p>
                <div className="mt-3 flex items-center gap-2 flex-wrap">
                  <code className="font-mono text-amber-400 font-bold text-lg bg-black/30 border border-white/10 rounded-md px-3 py-1.5" data-testid="referral-code">{referral.code}</code>
                  <Button size="sm" onClick={() => { navigator.clipboard.writeText(referral.share_url); toast.success("Link copied"); }} variant="outline" className="bg-transparent border-white/10 h-9" data-testid="referral-copy">
                    <Copy className="w-3.5 h-3.5 mr-1.5" /> Copy link
                  </Button>
                  <Button size="sm" onClick={async () => {
                    const msg = `Grab NZ$5 off your first Triptap ride with my code ${referral.code}: ${referral.share_url}`;
                    if (navigator.share) { try { await navigator.share({ title: "Triptap NZ$5 off", text: msg, url: referral.share_url }); } catch {} }
                    else { navigator.clipboard.writeText(msg); toast.success("Share message copied"); }
                  }} className="btn-gold rounded-full h-9" data-testid="referral-share">
                    <Share2 className="w-3.5 h-3.5 mr-1.5" /> Share
                  </Button>
                </div>
              </div>
              <div className="text-right shrink-0">
                <div className="text-xs uppercase tracking-widest font-mono text-slate-500">Balance</div>
                <div className="font-display text-2xl font-extrabold text-emerald-400" data-testid="referral-credits">NZ$ {referral.credits_nzd.toFixed(2)}</div>
                <div className="text-[10px] text-slate-500 font-mono mt-0.5">{referral.referral_count} friend{referral.referral_count === 1 ? "" : "s"} joined</div>
              </div>
            </div>
          </div>
        )}

        {/* Push notifications enable — one-tap opt-in for PWA/mobile users */}
        {"PushManager" in window && !pushOn && (
          <button onClick={enablePush} className="mt-4 w-full glass-card rounded-xl p-3 border border-red-500/30 hover:border-red-500/60 flex items-center gap-3 text-left transition-colors" data-testid="push-enable">
            <div className="w-9 h-9 rounded-lg bg-red-500/15 grid place-items-center shrink-0"><Bell className="w-4 h-4 text-red-400" /></div>
            <div className="flex-1">
              <div className="text-sm font-medium text-white">Turn on ride alerts</div>
              <div className="text-xs text-slate-400">Get a native buzz the moment your driver is 5 minutes out.</div>
            </div>
            <div className="text-xs font-mono uppercase tracking-widest text-red-300">Enable →</div>
          </button>
        )}

        {me?.loyalty?.enabled && (
          <div className="mt-6 glass-card rounded-2xl p-5 gold-border flex items-center gap-4" data-testid="loyalty-progress">
            <div className="w-11 h-11 rounded-lg bg-amber-400/10 grid place-items-center"><Gift className="w-5 h-5 text-amber-400" /></div>
            <div className="flex-1">
              <div className="font-display font-bold">Loyalty rewards</div>
              <p className="text-sm text-slate-400">{me.loyalty.rides_to_reward === 0 ? `Your next ride is ${me.loyalty.discount_percent}% off!` : `${me.loyalty.rides_to_reward} more ride${me.loyalty.rides_to_reward !== 1 ? "s" : ""} until ${me.loyalty.discount_percent}% off.`}</p>
              <div className="mt-2 h-1.5 bg-white/5 rounded-full overflow-hidden"><div className="h-full bg-gradient-to-r from-amber-400 to-amber-600" style={{ width: `${100 - (me.loyalty.rides_to_reward / me.loyalty.every_n_rides) * 100}%` }} /></div>
            </div>
            <div className="font-display text-3xl font-extrabold text-amber-400">{me.completed_rides}</div>
          </div>
        )}

        <div className="mt-8">
          <h2 className="font-display text-lg font-bold flex items-center gap-2 mb-3"><Star className="w-4 h-4 text-amber-400" /> Saved addresses</h2>
          <div className="grid sm:grid-cols-2 gap-3">
            {(me?.saved_addresses || []).map((a) => (
              <div key={a.id} className="glass-card rounded-xl p-4 gold-border flex items-start gap-3" data-testid={`addr-${a.label}`}>
                {a.label.toLowerCase() === "home" ? <Home className="w-4 h-4 text-amber-400 mt-1" /> : <Briefcase className="w-4 h-4 text-amber-400 mt-1" />}
                <div className="flex-1"><div className="font-mono text-xs uppercase tracking-wider text-amber-400">{a.label}</div><div className="text-sm mt-0.5">{a.address}</div></div>
                <button onClick={() => delAddr(a.id)} className="text-slate-500 hover:text-red-400"><Trash2 className="w-3.5 h-3.5" /></button>
              </div>
            ))}
            <form onSubmit={addAddr} className="glass-card rounded-xl p-4 gold-border sm:col-span-2 space-y-2">
              <div className="flex gap-2">
                <Input value={newAddr.label} onChange={(e) => setNewAddr({ ...newAddr, label: e.target.value })} placeholder="Home, Work…" className="bg-black/30 border-white/10 h-10 w-32 text-sm" data-testid="addr-label" />
                <div className="flex-1">
                  <AddressAutocomplete
                    data-testid="addr-address"
                    value={newAddr.address}
                    onChange={(v) => setNewAddr({ ...newAddr, address: v })}
                    placeholder="Search full address…"
                    className="bg-black/30 border-white/10 h-10 text-sm"
                  />
                </div>
                <Button size="sm" type="submit" className="btn-gold h-10" data-testid="addr-save"><Plus className="w-3.5 h-3.5" /></Button>
              </div>
            </form>
          </div>
        </div>

        <div className="mt-8 flex items-center justify-between">
          <h2 className="font-display text-lg font-bold">Ride history</h2>
          <Link to="/book"><Button className="btn-gold rounded-full"><Plus className="w-4 h-4 mr-2" /> New ride</Button></Link>
        </div>

        <div className="mt-4 space-y-3">
          {bookings.length === 0 && <div className="glass-card rounded-2xl p-8 text-center text-slate-500 gold-border">No rides yet. <Link to="/book" className="text-amber-400">Book your first →</Link></div>}
          {bookings.map((b) => (
            <div key={b.id} className="glass-card rounded-2xl p-5 gold-border" data-testid={`myride-${b.reference}`}>
              <div className="flex items-start justify-between gap-3">
                <div>
                  <Link to={`/confirmation/${b.reference}`} className="font-mono text-amber-400 font-bold hover:underline">#{b.reference}</Link>
                  <div className="text-xs text-slate-500 mt-1">{new Date(b.created_at).toLocaleString()}</div>
                </div>
                <Badge className={`${STATUS_COLORS[b.status]} border font-mono uppercase text-[10px] tracking-wider`}>{b.status.replace("_", " ")}</Badge>
              </div>
              <div className="mt-3 pt-3 border-t border-white/5 text-sm space-y-1">
                <div className="flex items-start gap-2"><MapPin className="w-3.5 h-3.5 text-emerald-400 mt-0.5" /><span className="text-slate-300">{b.pickup_location}</span></div>
                <div className="flex items-start gap-2"><MapPin className="w-3.5 h-3.5 text-red-400 mt-0.5" /><span className="text-slate-300">{b.dropoff_location}</span></div>
              </div>
              <div className="mt-3 flex items-center justify-between">
                <div className="text-xs text-slate-500">{b.service_name} · <span className="font-mono text-amber-400 font-bold">NZ$ {Number(b.fare_estimate).toFixed(2)}</span> {b.loyalty_applied && <span className="text-amber-400 ml-1">🎁 loyalty</span>}</div>
                <Button size="sm" onClick={() => rebook(b)} className="btn-gold rounded-full h-8 text-xs" data-testid={`rebook-${b.reference}`}><Repeat className="w-3 h-3 mr-1.5" /> Book again</Button>
              </div>
            </div>
          ))}
        </div>

        {/* DSAR (Privacy Act 2020 / GDPR) — self-serve so support isn't a bottleneck */}
        <div className="mt-12 pt-8 border-t border-white/5" data-testid="dsar-panel">
          <div className="text-caption mb-1">Your data</div>
          <h2 className="font-display text-lg font-bold">Privacy controls</h2>
          <p className="text-slate-400 text-sm mt-1">Download everything we hold on you, or close your account and have your profile anonymised.</p>
          <div className="mt-4 grid sm:grid-cols-2 gap-3">
            <button
              onClick={downloadData}
              disabled={dsarBusy}
              className="glass-card rounded-xl p-4 gold-border text-left hover:border-amber-400/40 transition-colors disabled:opacity-50"
              data-testid="dsar-download"
            >
              <div className="flex items-center gap-2 text-amber-400 font-display font-bold"><Download className="w-4 h-4" /> Download my data</div>
              <div className="text-xs text-slate-400 mt-1.5 leading-relaxed">Full JSON export of your profile, ride history, saved addresses, and contact messages.</div>
            </button>
            <button
              onClick={() => setConfirmDelete(true)}
              disabled={dsarBusy}
              className="glass-card rounded-xl p-4 border border-red-500/30 hover:border-red-500/60 transition-colors text-left disabled:opacity-50"
              data-testid="dsar-delete"
            >
              <div className="flex items-center gap-2 text-red-400 font-display font-bold"><ShieldOff className="w-4 h-4" /> Delete my account</div>
              <div className="text-xs text-slate-400 mt-1.5 leading-relaxed">Anonymises your profile immediately. Ride history is kept but stripped of your name, email, and phone number.</div>
            </button>
          </div>
        </div>

        <AlertDialog open={confirmDelete} onOpenChange={setConfirmDelete}>
          <AlertDialogContent className="bg-[#0b1220] border-red-500/30 text-white" data-testid="dsar-confirm-dialog">
            <AlertDialogHeader>
              <AlertDialogTitle className="flex items-center gap-2 text-red-400">
                <AlertTriangle className="w-5 h-5" /> Delete your account?
              </AlertDialogTitle>
              <AlertDialogDescription className="text-slate-400 leading-relaxed">
                This anonymises your profile immediately. Your ride history is kept for tax reporting but stripped of your name, email, and phone. This can't be undone — you'll need to register again to book new rides.
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel className="bg-transparent border-white/10 text-slate-300 hover:text-white" data-testid="dsar-cancel">Keep account</AlertDialogCancel>
              <AlertDialogAction onClick={deleteAccount} disabled={dsarBusy} className="bg-red-500 hover:bg-red-600 text-white" data-testid="dsar-confirm">
                Yes, delete
              </AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>
      </div>
    </div>
  );
}
